import React, { useContext, useEffect, useRef, useState } from 'react';
import AtomicButton from '../../atomic/button/Button';
import InputBox from '../../atomic/inputbox/InputBox';
import SelectBox from '../../atomic/selectbox/SelectBox';
import ChatGPTStyleMessage from '../ChatGptTypingMsg/ChatGPTStyleMessage';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import Dialog from '@mui/material/Dialog';
import ImageUploader from './ImageUpLoader';
import OfferSelector from './PromotionRadioBtn';
import WeekdayCheckboxes from './ServingDaysCheckBox';
import { CircularProgress, Snackbar, TextField, Typography } from '@mui/material';
import IngredientsInput from './IngredientInput';
import PriceInput from './Price';
import ExtraToppingsInput from './ExtraTopping';
import ConfirmationCheckbox from './ConfirmationCheckbox';
import { ProductDataContext } from '../../context-api/data-provider/ProductDataProvider';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import AllergicIngredientsInput from './AllergicIngredients';
import ErrorFormAlert from './ErrorForm';
import responseErrorMapping from '../../utils/error.response.mapping';
import '../../pages/order/dashboard.scss';
import { API_NETWORK_ERROR, localStorageKeys } from '../../utils/constants';
import { Error404 } from '../error404/Error404';
import TextTransformDatabaseEnum from '../../utils/textTransformDatabaseEnum';
import ProductNameAiDialog from './ProductNameAiDialog';
import ProductDescriptionAiDialog from './ProductDescriptionAiDialog';
import AiImageGenerator from './AiImageGenerator';
import Swal from 'sweetalert2';
import ComboCategoryDialog from './ComboCategoryDialog';
import { KitchenOperationUpdateContext } from '../../context-api/data-provider/UpdateOperationDataProvider';
import { containsUnsafeScriptContent } from '../../utils/regexValidation';

const isValidDescription = ({ text, name, setInputValidationError }) => {
  setInputValidationError(false);
  if (name === 'recipewith' && !text.trim()) return '';
  if (!text.trim()) return 'Description is required';

  if (name === 'productname' && text.length > 50) {
    setInputValidationError(true);
    return 'Max 50 characters allowed';
  }

  if (name === 'recipewith' && text.length > 25) {
    setInputValidationError(true);
    return 'Max 25 characters allowed';
  }

  if (name === 'quantity' && text.length > 5) {
    setInputValidationError(true);
    return 'Max 5 characters allowed';
  }

  if (name === 'description' && text.length > 500) {
    setInputValidationError(true);
    return 'Max 500 characters allowed';
  }

  if (name === 'quantity' && !/^\d+$/.test(text)) {
    setInputValidationError(true);
    return 'Only numbers allowed';
  }

  if (containsUnsafeScriptContent(text)) {
    setInputValidationError(true);
    return 'Script or unsafe content is not allowed';
  }
  return '';
};

/**
 * Generates a cryptographically random n-digit number (e.g. 9 digits => 100000000–999999999).
 * Uses crypto.getRandomValues to avoid collisions; safe for use as SKU on each API hit.
 */
const generateRandomNDigits = (n) => {
  if (typeof n !== 'number' || n < 1 || n > 15) {
    n = 9;
  }
  const bytes = new Uint8Array(n);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < n; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  let value = (bytes[0] % 9) + 1;
  for (let i = 1; i < n; i++) {
    value = value * 10 + (bytes[i] % 10);
  }
  return value;
};

function filterComboCategoriesByMealOffering(categories, mealOffering) {
  if (!Array.isArray(categories) || categories.length === 0) return ['select'];
  if (!mealOffering || typeof mealOffering !== 'object') return categories;
  const filtered = categories.filter((cat) => {
    if (cat === 'select') return true;
    if (!Object.prototype.hasOwnProperty.call(mealOffering, cat)) return false;
    const n = Number(mealOffering[cat]);
    return !Number.isNaN(n) && n > 0;
  });
  return filtered.length ? filtered : ['select'];
}

function RestaurantProducts(props) {
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const { onLoadInfoResponse, OnLoadInformation } = useContext(GlobalStateDataContext);
  const { onKitchenOperationUpdateAPI, kitchenUpdateAPIResponse } = useContext(
    KitchenOperationUpdateContext
  );

  const {
    onAddProduct,
    setAddProductData,
    addProductData,
    onUploadImageCludinary,
    setUploadImage,
    uploadImage,
    onGetProductCategory,
    productCategories,
  } = useContext(ProductDataContext);

  const { data, success, loading, errorMessage } = addProductData;
  const { loading: isLoading } = uploadImage;
  const { loading: categoryLoading } = productCategories;
  const ApiError = data?.errors;

  // console.log('---data', data);
  const handleClose = (event, reason) => {
    setOpenSnackBar(false);

    if (reason === 'clickaway') {
      return;
    }
  };

  const [offers, setOffers] = useState({
    buy_one_get_one_free: false,
    buy_two_get_one_free: false,
    buy_one_get_half_price: false,
    none: true, // Default selected
  });
  const [agreed, setAgreed] = useState(false);
  const [comparePrice, setComparePrice] = useState(false);
  const [openProductNameDialog, setOpenProductNameDialog] = useState(false);
  const [openProductDescripDialog, setOpenProductDescripDialog] = useState(false);
  const [btnSelected, setBtnSelected] = useState('non_meal_item');
  const [openComboCategoryDialog, setOpenComboCategoryDialog] = useState(false);
  const [imageFile, setImageFile] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [openAiDialog, setOpenAiDialog] = useState(false);
  const [aiValidationSnackbar, setAiValidationSnackbar] = useState({ open: false, message: '' });
  const [ingredientImageFile, setIngredientImageFile] = useState('');
  const [ingredientPreviewUrl, setIngredientPreviewUrl] = useState('');
  const bizOptions = ['Cloud kitchen', 'Restaurant', 'Home chef', 'Catering'];
  const cuisineOptions = [
    'Indian',
    'Mexican',
    'American chinese',
    'Italian',
    'Lebanese',
    'Turkish',
    'Fast food',
    'Chinese',
    'Mix of all',
  ];

  const initialExtraToppingRow = {
    topping_name: '',
    topping_price: '',
    topping_available_quantity: '',
    errors: {},
  };

  const [productName, setProductName] = useState('');
  const [recipeWith, setRecipeWith] = useState('');
  const [extraToppings, setExtraToppings] = useState([{ ...initialExtraToppingRow }]);

  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({
    productname: '',
    recipewith: '',
    description: '',
    quantity: '',
  });

  const [inputValidationError, setInputValidationError] = useState(false);
  const [productCategory, setProductCategory] = useState('select');
  const [specialMarking, setSpecialMarking] = useState('select');
  const [servingTemperature, setServingTemperature] = useState('select');
  const [mealType, setMealType] = useState('all day');
  const [size, setSize] = useState('full');
  const [servingSize, setServingSize] = useState('one_person');
  const KitchenName = onLoadInfoResponse?.data?.data?.data?.KitchenInfo?.kitchenName;
  const [sku, setSku] = useState(generateRandomNDigits(9));
  const [price, setPrice] = useState('');
  const [allergicIngredients, setAllergicIngredients] = useState(['']);
  const [ingredients, setIngredients] = useState(['']);
  const SpecialMarkingList = [
    'select',
    'non veg',
    'vegetarian',
    'kosher',
    'halal',
    'gluten free',
    'ud',
    'u',
    'none',
  ];
  const servingTemperatureList = ['select', 'frozen', 'cold', 'warm', 'hot'];
  const Cuisine = onLoadInfoResponse?.data?.data?.data?.KitchenInfo?.cuisine;
  const AccountType = onLoadInfoResponse?.data?.data?.data?.KitchenInfo?.account_type;
  const MealOffering = onLoadInfoResponse?.data?.data?.data?.kitchenOperationObject?.meal_offering;
  const AiImageAllow =
    onLoadInfoResponse?.data?.data?.data?.kitchenOperationObject?.IsAiImageGenerate;

  const [cuisineType, setCuisineType] = useState('select');
  const cuisineList = [
    'select',
    'indian',
    'mexican',
    'american chinese',
    'italian',
    'lebanese',
    'turkish',
    'fast food',
    'chinese',
    'mediterranean',
    'mix of all',
  ];
  useEffect(() => {
    onGetProductCategory();
  }, []);

  const onCategoryRefresh = () => {
    onGetProductCategory({ forceRefresh: true });
  };

  const apiCategoryData = productCategories?.data || {};
  const RegularMenuCategory = apiCategoryData?.RegularMenuCategory || [];
  const ComboMenuCategory = apiCategoryData?.ComboMenuCategory || [];
  const PromotionalStoreCategory = apiCategoryData?.PromotionalStoreCategory || [];
  const CloudKitchenComboMenuCategory = apiCategoryData?.CloudKitchenComboMenuCategory || [];

  const SizeList = ['full', 'half'];

  const CategoryList = () => {
    if (AccountType === 'restaurant') {
      return btnSelected === 'non_meal_item'
        ? RegularMenuCategory
        : filterComboCategoriesByMealOffering(ComboMenuCategory, MealOffering);
    } else if (AccountType === 'cloud kitchen') {
      return btnSelected === 'non_meal_item'
        ? CloudKitchenComboMenuCategory
        : filterComboCategoriesByMealOffering(CloudKitchenComboMenuCategory, MealOffering);
    } else if (AccountType === 'promotional kitchen') {
      return btnSelected === 'non_meal_item'
        ? PromotionalStoreCategory
        : filterComboCategoriesByMealOffering(PromotionalStoreCategory, MealOffering);
    }
    return ['select'];
  };

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const [selectedDays, setSelectedDays] = useState([...daysOfWeek]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');
  const [quantity, setQuantity] = useState(1000);
  const [missingFields, setMissingFields] = useState([]);
  const formTopRef = useRef(null);
  const productImageCacheRef = useRef({ cacheKey: '', secureUrl: '' });
  const ingredientImageCacheRef = useRef({ cacheKey: '', secureUrl: '' });

  const getFileCacheKey = (file) =>
    file && file.name != null ? `${file.name}-${file.size}-${file.lastModified ?? 0}` : '';

  const getFieldError = (param) => {
    const found = missingFields.find((m) => m.param === param);
    return found ? found.errorMessage : '';
  };

  const getProductPromotion = (list) => {
    const validPromotions = Object.entries(list).find(
      ([key, value]) => value === true && key !== 'none'
    );

    return validPromotions
      ? { product_promotion: { [validPromotions[0]]: true } }
      : { product_promotion: {} };
  };

  const resultPromotion = getProductPromotion(offers);
  // Helper to format price to decimal
  const formatPrice = (raw = '') => {
    const digitsOnly = raw.replace(/\D/g, '').padStart(3, '0');
    const intPart = digitsOnly.slice(0, -2);
    const decimalPart = digitsOnly.slice(-2);
    return parseFloat(`${parseInt(intPart, 10)}.${decimalPart}`);
  };

  // Clean each Extra topping object

  const cleanedToppings = extraToppings.filter(
    (t) => t.topping_name.trim() !== '' && Number(t.topping_price) !== 0
  );
  const cleanExtraToppings = cleanedToppings.map(
    ({ errors, topping_price, topping_available_quantity, ...rest }) => ({
      ...rest,
      topping_price: formatPrice(topping_price),
      topping_available_quantity: parseInt(topping_available_quantity, 10),
    })
  );

  const cleanedIngredients = [...new Set(ingredients.filter((item) => item.trim() !== ''))];
  const cleanedAllergicIngredients = [
    ...new Set(allergicIngredients.filter((item) => item.trim() !== '')),
  ];

  const ApiPayload = {
    kitchen_name: KitchenName,
    product_sku: sku,
    product_category: TextTransformDatabaseEnum(productCategory),
    product_name: productName,
    recipe_with: recipeWith,
    product_description: description,
    special_marking: specialMarking,
    serving_temperature: servingTemperature === 'select' ? '' : servingTemperature,
    cuisine: cuisineType,
    serving_days: selectedDays,
    is_available_whole_week: true, // Need check what is this for
    content_confirmation: agreed,
    price: price,
    available_quantity: parseInt(quantity, 10),
    product_type: btnSelected,
    serving_size: servingSize,
    size: size,
    meal_type: mealType,
    ...(btnSelected === 'non_meal_item' &&
      cleanExtraToppings?.length > 0 && {
        extra_toppings: cleanExtraToppings,
      }),

    ...(btnSelected === 'non_meal_item' &&
      Object.keys(resultPromotion.product_promotion).length > 0 &&
      resultPromotion),

    ...(cleanedIngredients?.length > 0 && { ingredients: cleanedIngredients }),

    ...(cleanedAllergicIngredients?.length > 0 && {
      allergic_ingredients: cleanedAllergicIngredients,
    }),

    ingredients_image: '',

    image_url: '',
  };

  const onChangeHandler = (key) => (value) => {
    setInputValidationError(false);
    value.preventDefault();
    const val = value.target.value;

    const newErrors = { ...errors };

    if (key === 'productname') {
      setProductName(val);
      newErrors.productname = isValidDescription({
        text: val,
        name: 'productname',
        setInputValidationError,
      });
    }

    if (key === 'recipewith') {
      setRecipeWith(val);
      newErrors.recipewith = isValidDescription({
        text: val,
        name: 'recipewith',
        setInputValidationError,
      });
    }

    if (key === 'description') {
      setDescription(val);
      newErrors.description = isValidDescription({
        text: val,
        name: 'description',
        setInputValidationError,
      });
    }

    if (key === 'quantity') {
      setQuantity(val);
      newErrors.quantity = isValidDescription({
        text: val,
        name: 'quantity',
        setInputValidationError,
      });
    }
    setErrors(newErrors);

    if (imageFile) {
      const formData = new FormData();
      formData.append('image', imageFile);
      // Submit formData to server
    }
  };

  const priceTableText = `
    🍨Ben & Jerry’s Half Baked Dough Chunks – 6oz
    
    Store           | Price  | Note                          | Delivery | Coupon
    ---------------------------------------------------------------
    Walmart         | $4.98  | Free pickup                   | 🚚       | -
    Target          | $5.29  | Same-day delivery             | 🚚       | -
    Amazon Fresh    | $5.49  | Prime only – 2hr shipping     | 🚚       | -
    Whole Foods     | $5.99  | Price varies by location      | 🚚       | -
    Kroger          | $5.25  | Loyalty card saves $0.50      | ❌       | 🎟️
    Safeway         | $5.75  | Coupon SAVE2 available        | 🚚       | 🎟️
    GoPuff          | $6.49  | 30 min delivery               | 🚚       | -
    `;

  const BtnClicked = (value) => {
    setAddProductData({
      loading: false,
      data: null,
      success: null,
      errorMessage: '',
    });

    if (value === 'non_meal_item') {
      setOpenComboCategoryDialog(false);
      setBtnSelected('non_meal_item');
      productImageCacheRef.current = { cacheKey: '', secureUrl: '' };
      ingredientImageCacheRef.current = { cacheKey: '', secureUrl: '' };
      setProductName('');
      setRecipeWith('');
      setProductCategory('select');
      setSpecialMarking('select');
      setServingTemperature('select');
      setPrice('');
      setQuantity(1000);
      setAllergicIngredients(['']);
      setIngredients(['']);
      setDescription('');
      setImageFile('');
      setPreviewUrl('');
      setIngredientImageFile('');
      setIngredientPreviewUrl('');
      setExtraToppings([{ ...initialExtraToppingRow }]);
      setMissingFields([]);
      setAgreed(false);
      setSize('full');
      setServingSize('one_person');
      setMealType('all day');
      setCuisineType('select');
      setSku(generateRandomNDigits(9));
      setSelectedDays([...daysOfWeek]);
      setSelectedTitle('');
      setSelectedDescription('');
      setErrors({ productname: '', recipewith: '', description: '', quantity: '' });
      setInputValidationError(false);
      setOffers({
        buy_one_get_one_free: false,
        buy_two_get_one_free: false,
        buy_one_get_half_price: false,
        none: true, // Default selected
      });
    }

    if (value === 'meal_item') {
      setBtnSelected('meal_item');
      productImageCacheRef.current = { cacheKey: '', secureUrl: '' };
      ingredientImageCacheRef.current = { cacheKey: '', secureUrl: '' };
      setProductName('');
      setRecipeWith('');
      setProductCategory('select');
      setSpecialMarking('select');
      setServingTemperature('select');
      setPrice('');
      setQuantity(1000);
      setAllergicIngredients(['']);
      setIngredients(['']);
      setDescription('');
      setImageFile('');
      setPreviewUrl('');
      setIngredientImageFile('');
      setIngredientPreviewUrl('');
      setExtraToppings([{ ...initialExtraToppingRow }]);
      setMissingFields([]);
      setAgreed(false);
      setSize('full');
      setServingSize('one_person');
      setMealType('all day');
      setCuisineType('select');
      setSku(generateRandomNDigits(9));
      setSelectedDays([...daysOfWeek]);
      setSelectedTitle('');
      setSelectedDescription('');
      setErrors({ productname: '', recipewith: '', description: '', quantity: '' });
      setInputValidationError(false);
      setOffers({
        buy_one_get_one_free: false,
        buy_two_get_one_free: false,
        buy_one_get_half_price: false,
        none: true, // Default selected
      });
    }
  };

  const handleAddProduct = async () => {
    setMissingFields([]);
    setOpenSnackBar(false);
    setAddProductData({
      loading: false,
      data: null,
      success: null,
      errorMessage: '',
    });
    const errorMsg = [];

    if (inputValidationError) {
      errorMsg.push({
        errorMessage: 'Please check your input — a required field may be missing or invalid.',
        param: 'input_box',
      });
    }

    if (ApiPayload?.product_name === null || ApiPayload?.product_name === '') {
      errorMsg.push({
        errorMessage: 'product_name is required',
        param: 'product_name',
      });
    }

    if (ApiPayload?.product_sku === null) {
      errorMsg.push({
        errorMessage: 'product sku is required',
        param: 'product_sku',
      });
    }

    if (ApiPayload?.price === '') {
      errorMsg.push({
        errorMessage: 'price is required 1',
        param: 'price',
      });
    }

    if (ApiPayload?.serving_days.length <= 0) {
      errorMsg.push({
        errorMessage: 'Serving days required',
        param: 'serving_days',
      });
    }

    // if (ApiPayload?.serving_temperature === 'select') {
    //   errorMsg.push({
    //     errorMessage: 'Serving temperature required',
    //     param: 'serving_temperature',
    //   });
    // }

    if (ApiPayload?.special_marking === 'select') {
      errorMsg.push({
        errorMessage: 'Special marking required',
        param: 'special_marking',
      });
    }

    if (ApiPayload?.product_category === 'select') {
      errorMsg.push({
        errorMessage: 'product_category is required',
        param: 'product_category',
      });
    }

    if (ApiPayload?.cuisine === 'select' || !ApiPayload?.cuisine?.trim()) {
      errorMsg.push({
        errorMessage: 'Cuisine is required',
        param: 'cuisine',
      });
    }

    if (ApiPayload?.product_description === '') {
      errorMsg.push({
        errorMessage: 'Product description is required',
        param: 'product_description',
      });
    }

    // if (ApiPayload?.recipe_with == null || String(ApiPayload?.recipe_with ?? '').trim() === '') {
    //   errorMsg.push({
    //     errorMessage: 'Recipe with is required',
    //     param: 'recipe_with',
    //   });
    // }

    if (btnSelected === 'non_meal_item' && extraToppings?.length) {
      extraToppings.forEach((t, index) => {
        const hasName = t.topping_name?.trim();
        if (!hasName) return;
        const missing = [];
        const priceVal = t.topping_price;
        const qtyVal = t.topping_available_quantity;
        const hasPrice =
          priceVal !== undefined &&
          priceVal !== null &&
          String(priceVal).trim() !== '' &&
          Number(parseFloat(String(priceVal).replace(/\D/g, ''))) > 0;
        const hasQty =
          qtyVal !== undefined &&
          qtyVal !== null &&
          String(qtyVal).trim() !== '' &&
          !Number.isNaN(Number(qtyVal)) &&
          Number(qtyVal) >= 0;
        if (!hasPrice) missing.push('price');
        if (!hasQty) missing.push('quantity');
        if (missing.length) {
          errorMsg.push({
            errorMessage: `Extra topping "${hasName}" (row ${index + 1}): please add ${missing.join(' and ')}.`,
            param: 'extra_topping',
          });
        }
      });
    }

    if (errorMsg.length > 0) {
      setMissingFields(errorMsg);
      setTimeout(() => {
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return;
    }

    const requestSku = generateRandomNDigits(9);
    setSku(requestSku);

    let imageUrl = '';
    let secureUrl = '';
    let ingredientImageUrl = '';
    let ingredientSecureUrl = '';

    if (imageFile && KitchenName) {
      const productKey = getFileCacheKey(imageFile);
      const cached = productImageCacheRef.current;
      if (cached.cacheKey === productKey && cached.secureUrl) {
        secureUrl = cached.secureUrl;
      } else {
        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('fileName', `${KitchenName}_${requestSku}`);
        formData.append('kitchen_name', KitchenName);
        const response = await onUploadImageCludinary(formData);

        if (response?.data?.success) {
          // eslint-disable-next-line no-unused-vars
          imageUrl = response?.data?.url;
          secureUrl = response?.data?.secureUrl;
          productImageCacheRef.current = { cacheKey: productKey, secureUrl };
        }

        if (response?.data?.success === false) {
          errorMsg.push({
            errorMessage:
              'Something went wrong during product image upload. Please try again later or contact support.',
            param: 'file',
          });

          if (errorMsg.length > 0) {
            setMissingFields(errorMsg);
          }

          return;
        }
      }
    } else {
      productImageCacheRef.current = { cacheKey: '', secureUrl: '' };
    }

    if (ingredientImageFile && KitchenName) {
      const ingredientKey = getFileCacheKey(ingredientImageFile);
      const cachedIng = ingredientImageCacheRef.current;
      if (cachedIng.cacheKey === ingredientKey && cachedIng.secureUrl) {
        ingredientSecureUrl = cachedIng.secureUrl;
      } else {
        const formData = new FormData();
        formData.append('file', ingredientImageFile);
        formData.append('fileName', `${KitchenName}_${'ingredientImage'}_${requestSku}`);
        formData.append('kitchen_name', KitchenName);
        const responseTwo = await onUploadImageCludinary(formData);

        if (responseTwo?.data?.success) {
          // eslint-disable-next-line no-unused-vars
          ingredientImageUrl = responseTwo.data?.url;
          ingredientSecureUrl = responseTwo?.data?.secureUrl;
          ingredientImageCacheRef.current = {
            cacheKey: ingredientKey,
            secureUrl: ingredientSecureUrl,
          };
        }
      }
    } else {
      ingredientImageCacheRef.current = { cacheKey: '', secureUrl: '' };
    }

    const Respons = await onAddProduct({
      ...ApiPayload,
      product_sku: requestSku,
      image_url: secureUrl,
      ingredients_image: ingredientSecureUrl,
    });
    if (Respons?.success && Respons?.CustomMessage === 'Successfully created new product') {
      Swal.fire({
        icon: 'success',
        title: 'Product created',
        text: 'Successfully created new product',
      });
      productImageCacheRef.current = { cacheKey: '', secureUrl: '' };
      ingredientImageCacheRef.current = { cacheKey: '', secureUrl: '' };
      setSku(generateRandomNDigits(9));
      setProductName('');
      setRecipeWith('');
      setProductCategory('select');
      setSpecialMarking('select');
      setServingTemperature('select');
      setPrice('');
      setQuantity(1000);
      setAllergicIngredients(['']);
      setIngredients(['']);
      setDescription('');
      setImageFile('');
      setPreviewUrl('');
      setIngredientImageFile('');
      setIngredientPreviewUrl('');
      setExtraToppings([{ ...initialExtraToppingRow }]);
      setAgreed(false);
      setSize('full');
      setServingSize('one_person');
      setMealType('all day');
      setCuisineType('select');
      setSelectedDays([...daysOfWeek]);
      setSelectedTitle('');
      setSelectedDescription('');
      setErrors({ productname: '', recipewith: '', description: '', quantity: '' });
      setInputValidationError(false);
      setOffers({
        buy_one_get_one_free: false,
        buy_two_get_one_free: false,
        buy_one_get_half_price: false,
        none: true, // Default selected
      });

      setTimeout(() => {
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      return;
    } else {
      // Somehing is wrong in API add product
      console.log('----Add Product API Error', Respons);
      errorMsg.push({
        errorMessage:
          'Something went seriously wrong. Please notify IT support. Reference #: 339966.',
        param: Respons?.message?.error,
      });

      if (errorMsg.length > 0) {
        setMissingFields(errorMsg);
      }
      setTimeout(() => {
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return;
    }
  };

  if (success === false && errorMessage === API_NETWORK_ERROR) {
    return <Error404 />;
  }

  return (
    <div
      ref={formTopRef}
      style={{ display: 'flex', flexDirection: 'column', margin: '20px', alignItems: 'center' }}
    >
      <div style={{ fontSize: '18px', fontFamily: 'Inter Bold', color: 'rgb(24, 20, 54)' }}>
        {' '}
        ADD PRODUCT
      </div>
      <div
        style={{
          fontSize: '14px',
          fontFamily: 'Inter Regular',
          color: 'gray',
          textWrap: 'wrap',
          width: '400px',
          letterSpacing: '0.5px',
          marginTop: '3px',
        }}
      >
        {' '}
        Use this form to add your product to the regular menu or include it as part of a combo
        menu.{' '}
      </div>

      <div
        style={{ display: 'flex', flexDirection: 'row', marginBottom: '10px', marginTop: '10px' }}
      >
        <AtomicButton
          label='Regular Menu'
          handleSubmit={() => BtnClicked('non_meal_item')}
          style={{
            width: '300px',
            color: btnSelected === 'non_meal_item' ? 'white' : 'gray',
            backgroundColor: btnSelected === 'non_meal_item' ? '#181436' : '#F5F5F5',
            borderBottom: btnSelected === 'non_meal_item' ? '1px solid rgb(255, 123, 41)' : 'none',
            marginRight: '15px',
          }}
          loading={false}
          disabled={false}
        />

        <AtomicButton
          label='Combo Menu'
          handleSubmit={() => BtnClicked('meal_item')}
          style={{
            width: '300px',
            color: btnSelected === 'meal_item' ? 'white' : 'gray',
            backgroundColor: btnSelected === 'meal_item' ? '#181436' : '#EEEEEE',
            borderBottom: btnSelected === 'meal_item' ? '1px solid rgb(255, 123, 41)' : 'none',
          }}
          loading={false}
          disabled={false}
        />
      </div>
      {btnSelected === 'meal_item' && (
        <>
          <div style={{ fontFamily: 'Inter Regular', fontSize: '14px' }}>
            {' '}
            Update Combo Menu Product Category{' '}
            <button
              onClick={() => {
                setOpenComboCategoryDialog(true);
              }}
            >
              {' '}
              Update
            </button>
          </div>
          {openComboCategoryDialog && (
            <ComboCategoryDialog
              openComboCategoryDialog={openComboCategoryDialog}
              setOpenComboCategoryDialog={setOpenComboCategoryDialog}
              MealOffering={MealOffering}
              ComboMenuCategory={ComboMenuCategory}
              updateLoading={kitchenUpdateAPIResponse?.loading}
              onUpdateMealOffering={async (updatedOffering) => {
                // const resp = await onKitchenOperationUpdateAPI({
                //   meal_offering: updatedOffering,
                // });
                setOpenComboCategoryDialog(false);
                // if (resp?.success) {
                  Swal.fire({ icon: 'success', title: 'Updated', text: 'Combo meal offering updated successfully.' });
                //   // OnLoadInformation();
                // } else {
                //   Swal.fire({ icon: 'error', title: 'Error', text: resp?.message || 'Failed to update meal offering.' });
                // }
              }}
            />
          )}
        </>
      )}

      {((success === false && ApiError?.length > 0) || missingFields.length > 0) && (
        <ErrorFormAlert data={ApiError || missingFields} />
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '10px',
          marginTop: '10px',
          width: '610px',
        }}
      >
        <InputBox
          type='text'
          label='Product name'
          size='small'
          sx={{
            width: '580px',
            height: 'auto',
            margin: '10px 10px 5px',
          }}
          name='productname'
          value={productName}
          maxLength='10'
          error={!!errors.productname || !!getFieldError('product_name')}
          inputErrorMsg={
            getFieldError('product_name')
              ? responseErrorMapping(getFieldError('product_name'))
              : errors.productname || 'You can use up to 50 characters.'
          }
          onChange={onChangeHandler('productname')}
        />

        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '50px',
            marginTop: '0px',
            marginBottom: '20px',
            height: '40px',
            fontSize: '12px',
            backgroundColor: '#81E7AF',
            border: '1px solid #03A791',
            borderRadius: '3px',
          }}
          onClick={() => {
            const hasTitle = productName?.trim();
            if (!hasTitle) {
              setAiValidationSnackbar({
                open: true,
                message:
                  'Please add a product name/title before using AI to generate product name ideas.',
              });
              return;
            }
            setOpenProductNameDialog(true);
          }}
        >
          <AutoAwesomeIcon style={{ color: 'black', fontSize: '15px', marginRight: '5px' }} />
          AI
        </button>
      </div>

      {/* {comparePrice && <ChatGPTStyleMessage text={priceTableText} />} */}
      {openProductNameDialog && (
        <ProductNameAiDialog
          openProductNameDialog={openProductNameDialog}
          setOpenProductNameDialog={setOpenProductNameDialog}
          setProductName={setProductName}
          selectedTitle={selectedTitle}
          setSelectedTitle={setSelectedTitle}
          title={productName}
          productCategory={productCategory}
          cuisineType={cuisineType}
        />
      )} 

      <InputBox
        type='text'
        label='Recipe with'
        size='small'
        sx={{
          width: '600px',
          height: 'auto',
          margin: '5px 10px',
        }}
        name='recipewith'
        value={recipeWith}
        maxLength='25'
        error={!!errors.recipewith || !!getFieldError('recipe_with')}
        inputErrorMsg={
          getFieldError('recipe_with')
            ? responseErrorMapping(getFieldError('recipe_with'))
            : errors.recipewith || 'You can use up to 25 characters.'
        }
        onChange={onChangeHandler('recipewith')}
      />

      <div style={{ display: 'flex', flexDirection: 'row', width: '605px', margin: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', width: '295px' }}>
          <SelectBox
            sx={{
              width: '250px',
              height: 'auto',
            }}
            label='Product Category'
            value={productCategory}
            onChange={(value) => {
              setProductCategory(value);
            }}
            options={CategoryList()}
            error={!!getFieldError('product_category')}
            helperText={
              getFieldError('product_category')
                ? responseErrorMapping(getFieldError('product_category'))
                : 'Mandatory *'
            }
          />
          <button
            type='button'
            onClick={onCategoryRefresh}
            disabled={categoryLoading}
            title='Reload category'
            style={{
              height: '40px',
              minWidth: '36px',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              background: categoryLoading ? '#F5F5F5' : '#FFFFFF',
              color: '#181436',
              cursor: categoryLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {categoryLoading ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <RefreshIcon sx={{ fontSize: 18 }} />
            )}
          </button>
        </div>

        <SelectBox
          sx={{
            width: '295px',
            height: 'auto',
            marginLeft: '10px',
          }}
          label='Special Marking'
          value={specialMarking}
          onChange={(value) => {
            setSpecialMarking(value);
          }}
          options={SpecialMarkingList}
          error={!!getFieldError('special_marking')}
          helperText={
            getFieldError('special_marking')
              ? responseErrorMapping(getFieldError('special_marking'))
              : 'Mandatory *'
          }
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '605px',
          height: 'auto',
          justifyContent: 'space-between',
          margin: '10px 10px 20px 10px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            label='Enter Description'
            multiline
            minRows={10}
            maxRows={20}
            fullWidth
            value={description}
            onChange={onChangeHandler('description')} // Add Decription field in mongo DB schema
            error={!!errors.description || !!getFieldError('product_description')}
            helperText={
              getFieldError('product_description')
                ? responseErrorMapping(getFieldError('product_description'))
                : errors.description || 'You can use up to 500 characters.'
            }
            InputLabelProps={{
              sx: {
                color: 'gray',
                display: 'flex',
                '&.Mui-focused': {
                  color: 'rgb(255, 123, 41)',
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
                width: 320,

                '& fieldset': {
                  // borderColor: '#ccc',
                },
                '&:hover fieldset': {
                  // borderColor: 'rgb(24, 20, 54)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgb(24, 20, 54)',
                },
              },
            }}
          />
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '0px',
              marginBottom: '0px',
              height: '25px',
              fontSize: '12px',
              backgroundColor: '#81E7AF',
              border: '1px solid #03A791',
              borderRadius: '3px',
            }}
            onClick={() => {
              const hasTitle = productName?.trim();
              if (!hasTitle) {
                setAiValidationSnackbar({
                  open: true,
                  message:
                    'Please add a product name/title before using AI to generate product name ideas.',
                });
                return;
              }
              setOpenProductDescripDialog(true);
            }}
          >
            <AutoAwesomeIcon style={{ color: 'black', fontSize: '15px', marginRight: '5px' }} />
            AI Product Description
          </button>
        </div>

        <WeekdayCheckboxes
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          error={
            getFieldError('serving_days') ? responseErrorMapping(getFieldError('serving_days')) : ''
          }
        />
      </div>

      {/* {comparePrice && <ChatGPTStyleMessage text={priceTableText} />} */}
      {openProductDescripDialog && (
        <ProductDescriptionAiDialog
          openProductDescripDialog={openProductDescripDialog}
          setOpenProductDescripDialog={setOpenProductDescripDialog}
          setDescription={setDescription}
          title={productName}
          productCategory={productCategory}
          cuisineType={cuisineType}
        />
      )} 

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: 'row',
          width: '610px',
        }}
      >
        <PriceInput
          price={price}
          setPrice={setPrice}
          btnSelected={btnSelected}
          productCategory={productCategory}
          submitError={getFieldError('price') ? responseErrorMapping(getFieldError('price')) : ''}
        />
        <InputBox
          type='number'
          label='Quantity'
          size='small'
          sx={{
            width: '100px',
            height: 'auto',
            marginBottom: '10px',
          }}
          name='quantity'
          value={quantity}
          // maxLength="3"
          error={!!errors.quantity || quantity === '' || quantity === '0'}
          helperText={errors.quantity && 'Max 5 characters.'}
          onChange={onChangeHandler('quantity')}
        />

        {(AccountType === 'grocery store' || AccountType === 'wholesale store') && (
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '130px',
              height: '40px',
              fontSize: '12px',
              marginBottom: '10px',
              marginLeft: '10px',
              backgroundColor: '#81E7AF',
              border: '1px solid #03A791',
              borderRadius: '3px',
            }}
            onClick={() => {
              setComparePrice(true);
            }}
          >
            <AutoAwesomeIcon style={{ color: 'black', fontSize: '15px', marginRight: '5px' }} />
            Compare Price
          </button>
        )}

        <SelectBox
          sx={{
            width:
              AccountType === 'grocery store' || AccountType === 'wholesale store'
                ? '280px'
                : '500px', // "500px"
            height: 'auto',
            margin: '0px 0px 8px 10px',
          }}
          label='Serving Temperature'
          value={servingTemperature}
          onChange={(value) => {
            setServingTemperature(value);
          }}
          options={servingTemperatureList}
          error={!!getFieldError('serving_temperature')}
          helperText={
            getFieldError('serving_temperature')
              ? responseErrorMapping(getFieldError('serving_temperature'))
              : 'Mandatory *'
          }
        />
      </div>

      {comparePrice && (AccountType === 'grocery store' || AccountType === 'wholesale store') && (
        <ChatGPTStyleMessage text={priceTableText} />
      )}

      <div
        style={{
          display: 'flex',
          width: '610px',
          flexDirection: 'row',
          marginTop: '10px',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              border: '1px solid lightgray',
              borderRadius: '5px',
              padding: '10px',
              width: '200px',
              height: AiImageAllow ? '250px' : '290px',
              // height: '215px',
            }}
          >
            <ImageUploader
              onChange={(file) => setImageFile(file)}
              imageFile={imageFile}
              setImageFile={setImageFile}
              previewUrl={previewUrl}
              setPreviewUrl={setPreviewUrl}
            />
          </div>

          {AiImageAllow && (
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '10px',
                marginBottom: '0px',
                height: '25px',
                fontSize: '12px',
                backgroundColor: '#81E7AF',
                border: '1px solid #03A791',
                borderRadius: '3px',
              }}
              onClick={() => {
                const hasTitle = productName?.trim();
                const hasCategory = productCategory && productCategory !== 'select';
                if (!hasTitle || !hasCategory) {
                  const missing = [];
                  if (!hasTitle) missing.push('title');
                  if (!hasCategory) missing.push('category');
                  setAiValidationSnackbar({
                    open: true,
                    message: `Please add product title and select a category before generating an AI image. Missing: ${missing.join(', ')}.`,
                  });
                  return;
                }
                setOpenAiDialog(true);
              }}
            >
              <AutoAwesomeIcon style={{ color: 'black', fontSize: '15px', marginRight: '5px' }} />
              Generate AI Image
            </button>
          )}
        </div>

        {btnSelected === 'non_meal_item' && (
          <div
            style={{
              padding: '20px',
              marginRight: '5px',
              width: '330px',
              border: '1px solid gray',
              borderRadius: '5px',
            }}
          >
            <Typography variant='h6' sx={{ color: 'rgb(24, 20, 54)' }}>
              🎁 Select an Offer
            </Typography>

            <div
              style={{
                fontFamily: 'Inter Regular',
                letterSpacing: '0.5px',
                fontSize: '13px',
                flexWrap: 'wrap',
                margin: '10px 10px 10px 05px',
                color: 'gray',
              }}
            >
              Offering promotions is a powerful way to attract more customers and increase your
              sales. These deals grab attention, encourage larger orders, and help build customer
              loyalty
            </div>
            <OfferSelector offers={offers} setOffers={setOffers} />
          </div>
        )}
      </div>

      {comparePrice && <ChatGPTStyleMessage text={priceTableText} />}

      {btnSelected === 'non_meal_item' && (
        <ExtraToppingsInput
          extraToppings={extraToppings}
          setExtraToppings={setExtraToppings}
          submitError={
            getFieldError('extra_topping')
              ? responseErrorMapping(getFieldError('extra_topping'))
              : ''
          }
        />
      )}

      <div
        style={{
          display: 'flex',
          width: '570px',
          flexDirection: 'row',
          marginTop: '10px',
          justifyContent: 'space-between',
          border: '1px solid lightgray',
          borderRadius: '3px',
          padding: '30px',
        }}
      >
        <IngredientsInput ingredients={ingredients} setIngredients={setIngredients} />
        <AllergicIngredientsInput
          allergicIngredients={allergicIngredients}
          setAllergicIngredients={setAllergicIngredients}
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '630px',
          flexDirection: 'row',
          marginTop: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '380px',
            border: '1px solid lightgray',
            borderRadius: '3px',
          }}
        >
          <SelectBox
            sx={{
              width: '295px',
              height: 'auto',
              margin: '10px',
            }}
            label='Size'
            value={size}
            onChange={(value) => {
              setSize(value);
            }}
            options={SizeList}
            // error={productCategory === 'select' && true}
            // helperText='Mandatory *'
          />

          <SelectBox
            sx={{
              width: '295px',
              height: 'auto',
              margin: '10px',
            }}
            label='Cuisine'
            value={cuisineType}
            onChange={(value) => {
              setCuisineType(value);
            }}
            options={cuisineList}
            error={!!getFieldError('cuisine')}
            helperText={
              getFieldError('cuisine')
                ? responseErrorMapping(getFieldError('cuisine'))
                : 'Mandatory *'
            }
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid lightgray',
            borderRadius: '5px',
            padding: '10px',
            width: '200px',
            height: '250px',
          }}
        >
          <ImageUploader
            onChange={(file) => setIngredientImageFile(file)}
            imageFile={imageFile}
            setImageFile={setImageFile}
            previewUrl={previewUrl}
            setPreviewUrl={setPreviewUrl}
            ingredientImageFile={ingredientImageFile}
            setIngredientImageFile={setIngredientImageFile}
            ingredientPreviewUrl={ingredientPreviewUrl}
            setIngredientPreviewUrl={setIngredientPreviewUrl}
            imageFor='ingredients-image'
          />
        </div>
      </div>

      <div
        style={{
          border: '1px solid black',
          width: '590px',
          padding: '20px',
          marginTop: '20px',
          borderRadius: '3px',
          backgroundColor: 'rgb(233, 245, 190)',
        }}
      >
        <ConfirmationCheckbox agreed={agreed} setAgreed={setAgreed} />

        <div style={{ marginLeft: !agreed ? '30px' : '' }}>
          <AtomicButton
            label='ADD PRODUCT'
            handleSubmit={handleAddProduct}
            style={{
              width: '550px',
              marginLeft: '30px',
              marginTop: '30px',
            }}
            loading={loading || isLoading}
            disabled={!agreed || loading || isLoading}
          />
        </div>
      </div>

      <Snackbar
        open={openSnackBar}
        autoHideDuration={6000}
        onClose={handleClose}
        message='Awesome! Your new product has been added'
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={'order-snackbar'}
        // action={action}
      />

      <Snackbar
        open={aiValidationSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setAiValidationSnackbar((p) => ({ ...p, open: false }))}
        message={aiValidationSnackbar.message}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        ContentProps={{ sx: { backgroundColor: '#d32f2f', color: '#fff' } }}
      />

      <Dialog
        open={openAiDialog}
        // onClose={handleClose}  // This will close dialog if it clicked anywhere on screen
        className='alert-dialog-wrapper ai-image-generator-dialog'
      >
        <AiImageGenerator
          productCategory={productCategory}
          title={productName?.trim() || ''}
          description={description?.trim() || ''}
          setOpenAiDialog={setOpenAiDialog}
        />
      </Dialog>
    </div>
  );
}

export default RestaurantProducts;
